import React, { useEffect, useState } from 'react';
import { Backdrop, CircularProgress, Dialog, DialogContent, IconButton, Tab, Tabs } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Email, Lock, Person, Phone, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import InputField from '../../../common/fields/InputField';
import PrimaryButton from '../../../common/buttons/PrimaryButton';
import useParentAuth from '../../../../hooks/useParentAuth';
import { validateParentLogin, validateParentRegister } from '../../../../validations/parentAuthValidation';
import { getApiErrorMessage, mapApiValidationErrors } from '../../../../utils/apiError';
import { getLandingRouteForUser } from '../../../../utils/auth';

const VIEW = {
  LOGIN: 'login',
  REGISTER: 'register',
};

const defaultViewFor = (defaultView) => (defaultView === VIEW.REGISTER ? VIEW.REGISTER : VIEW.LOGIN);

function LoginForm({ onClose, onSwitchToRegister }) {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useParentAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate(getLandingRouteForUser(user), { replace: true });
  }, [isAuthenticated, navigate, user]);

  const handleChange = (field) => (e) => {
    setFormData((current) => ({ ...current, [field]: e.target.value }));
    if (errors[field]) {
      const { [field]: _removed, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateParentLogin(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setSubmitError('');

      try {
      const response = await login(formData);
      onClose();
      navigate(getLandingRouteForUser(response.user), { replace: true });
    } catch (error) {
      const mappedErrors = mapApiValidationErrors(error.response?.data);
      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors);
      } else {
        setSubmitError(getApiErrorMessage(error, 'Unable to log in. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <h2 className="text-3xl font-bold text-light mb-2 text-center">Parent/Student Login</h2>
      <p className="text-gray-400 text-center mb-8">Sign in to manage parent/student access</p>

      {submitError ? (
        <div className="mb-4 rounded-lg border border-red-500/50 bg-red-500/10 p-3">
          <p className="text-center text-sm text-red-400">{submitError}</p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        <InputField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          disabled={loading}
          icon={Email}
        />

        <InputField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          error={!!errors.password}
          helperText={errors.password}
          disabled={loading}
          icon={Lock}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword((value) => !value)}
                  edge="end"
                  disabled={loading}
                  tabIndex={-1}
                  sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            },
          }}
        />

        <PrimaryButton type="submit" fullWidth disabled={loading}>
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Login'}
        </PrimaryButton>

        <div className="text-center text-sm text-gray-400">
          Need a parent account?{' '}
          <button
            type="button"
            className="font-medium text-secondary hover:text-accent"
            onClick={onSwitchToRegister}
            disabled={loading}
          >
            Register
          </button>
        </div>
      </form>
    </div>
  );
}

function RegisterForm({ onClose, onSwitchToLogin }) {
  const navigate = useNavigate();
  const { register, isAuthenticated, user } = useParentAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate(getLandingRouteForUser(user), { replace: true });
  }, [isAuthenticated, navigate, user]);

  const handleChange = (field) => (e) => {
    setFormData((current) => ({ ...current, [field]: e.target.value }));
    if (errors[field]) {
      const { [field]: _removed, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateParentRegister(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setSubmitError('');

    try {
      const response = await register(formData);
      if (response?.access_token || response?.refresh_token) {
        onClose();
        navigate(getLandingRouteForUser(response.user), { replace: true });
        return;
      }
      onSwitchToLogin();
    } catch (error) {
      const mappedErrors = mapApiValidationErrors(error.response?.data);
      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors);
      } else {
        setSubmitError(getApiErrorMessage(error, 'Unable to register. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <h2 className="text-3xl font-bold text-light mb-2 text-center">Parent Registration</h2>
      <p className="text-gray-400 text-center mb-8">Create a parent account</p>

      {submitError ? (
        <div className="mb-4 rounded-lg border border-red-500/50 bg-red-500/10 p-3">
          <p className="text-center text-sm text-red-400">{submitError}</p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField
            label="First Name"
            type="text"
            value={formData.first_name}
            onChange={handleChange('first_name')}
            error={!!errors.first_name}
            helperText={errors.first_name}
            disabled={loading}
            icon={Person}
          />
          <InputField
            label="Last Name"
            type="text"
            value={formData.last_name}
            onChange={handleChange('last_name')}
            error={!!errors.last_name}
            helperText={errors.last_name}
            disabled={loading}
            icon={Person}
          />
        </div>

        <InputField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          disabled={loading}
          icon={Email}
        />

        <InputField
          label="Phone Number"
          type="tel"
          value={formData.phone}
          onChange={handleChange('phone')}
          error={!!errors.phone}
          helperText={errors.phone}
          disabled={loading}
          icon={Phone}
          inputProps={{ inputMode: 'numeric', maxLength: 10, pattern: '0[0-9]{9}' }}
        />

        <InputField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          error={!!errors.password}
          helperText={errors.password}
          disabled={loading}
          icon={Lock}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword((value) => !value)}
                  edge="end"
                  disabled={loading}
                  tabIndex={-1}
                  sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            },
          }}
        />

        <InputField
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirm_password}
          onChange={handleChange('confirm_password')}
          error={!!errors.confirm_password}
          helperText={errors.confirm_password}
          disabled={loading}
          icon={Lock}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  edge="end"
                  disabled={loading}
                  tabIndex={-1}
                  sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            },
          }}
        />

        <PrimaryButton type="submit" fullWidth disabled={loading}>
          {loading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            'Create Parent Account'
          )}
        </PrimaryButton>

        <div className="text-center text-sm text-gray-400">
          Already have a parent account?{' '}
          <button
            type="button"
            className="font-medium text-secondary hover:text-accent"
            onClick={onSwitchToLogin}
            disabled={loading}
          >
            Login
          </button>
        </div>
      </form>
    </div>
  );
}

export default function StudentAuthModal({ open, onClose, defaultView = VIEW.LOGIN }) {
  const [view, setView] = useState(defaultViewFor(defaultView));

  useEffect(() => {
    setView(defaultViewFor(defaultView));
  }, [defaultView]);

  const handleClose = () => {
    setView(VIEW.LOGIN);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0,0,0,0.7)',
          },
        },
        paper: {
          sx: {
            background: 'linear-gradient(158deg, rgba(17,24,39,1) 0%, rgba(15,15,18,1) 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
          zIndex: 1,
          '&:hover': {
            background: 'rgba(229,64,4,0.1)',
            color: '#F6911D',
            transition: 'all 0.3s ease',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <div className="border-b border-white/10">
          <Tabs
            value={view === VIEW.LOGIN ? 0 : 1}
            onChange={(_, next) => setView(next === 0 ? VIEW.LOGIN : VIEW.REGISTER)}
            centered
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.6)',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: '60px',
                '&:hover': { color: 'rgba(246,145,29,0.8)' },
              },
              '& .Mui-selected': { color: '#F6911D !important' },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
                height: '3px',
              },
            }}
          >
            <Tab label="Login" />
            <Tab label="Register" />
          </Tabs>
        </div>

        <div className="p-6 h-full overflow-y-auto">
          {view === VIEW.LOGIN && (
            <LoginForm onClose={handleClose} onSwitchToRegister={() => setView(VIEW.REGISTER)} />
          )}
          {view === VIEW.REGISTER && (
            <RegisterForm onClose={handleClose} onSwitchToLogin={() => setView(VIEW.LOGIN)} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
